/**
 * Safe wrapper for despia-native bridge calls.
 * Uses the direct import pattern: import despia from 'despia-native'
 * Then calls: despia(command, keys?)
 */

import despia from "despia-native";

export const isNativeApp = (): boolean =>
  typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().includes("despia");

/**
 * Call a despia command safely, returning true if it succeeded.
 */
export async function safeDespia(command: string): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    await despia(command);
    return true;
  } catch (e) {
    console.warn(`despia command failed: ${command}`, e);
    return false;
  }
}

/**
 * Call a despia command that returns data, passing response keys to listen for.
 */
export async function safeDespiaWithResponse<T = Record<string, unknown>>(
  command: string,
  keys: string[],
): Promise<T | null> {
  if (!isNativeApp()) return null;
  try {
    const result = await despia(command, keys);
    return result as T;
  } catch (e) {
    console.warn(`despia command failed: ${command}`, e);
    return null;
  }
}

/**
 * Fire-and-forget a despia command (no await).
 */
export function fireAndForget(command: string): void {
  if (!isNativeApp()) return;
  try {
    despia(command);
  } catch { /* no-op */ }
}

/**
 * Direct access to the despia function for property reads.
 */
export { despia };
